// External Dependencies
import { Formik } from 'formik';
import PaymentIcon from '@mui/icons-material/Payment';
import { CreateInvoicePaymentRequest } from '@presto-assistant/api_types/api/admin/invoicePayments';
import { convertDollarsToCents, formatYupError } from '@presto-assistant/api_types/utils';
import Collapse from '@mui/material/Collapse';
import { InvoiceShowResponse } from '@presto-assistant/api_types/api/admin/invoices';
import Box from '@mui/material/Box';
import DownloadIcon from '@mui/icons-material/CloudDownload';
import { useCallback } from 'react';

// Internal Dependencies
import { useCreateInvoicePayment } from 'hooks/api/invoicePayments';
import {
  DetailCard, EnhancedAlert, ShowPageDataDisplay,
} from 'components/shared';
import { useDownloadDocumentUpload } from 'hooks/api/documentUploads';

// Local Dependencies
import IconButton from '@mui/material/IconButton';
import InvoicePaymentFormFields from './InvoicePaymentFormFields';

// Local Typings
interface InvoicePaymentFormProps {
  invoiceId: string;
  invoicePayment: InvoiceShowResponse['invoicePayment'];
}

// Component Definition
const InvoicePaymentForm = ({
  invoiceId,
  invoicePayment,
}: InvoicePaymentFormProps): JSX.Element => {
  const {
    error,
    isLoading: isCreatingInvoicePayment,
    mutate: createInvoicePayment,
  } = useCreateInvoicePayment();

  const initialValues: CreateInvoicePaymentRequest['body'] = {
    amountInCents: 0,
    checkDate: new Date().toISOString().split('T')[0],
    checkImageDocumentId: '',
    checkNumber: '',
    invoiceId,
  };

  const handleSubmit = (values: typeof initialValues) => {
    // Convert amount to cents for API
    const amountInCents = convertDollarsToCents(values.amountInCents);

    // Create payload with only the required fields
    const payload: CreateInvoicePaymentRequest['body'] = {
      amountInCents,
      checkDate: values.checkDate,
      checkImageDocumentId: values.checkImageDocumentId,
      checkNumber: values.checkNumber,
      invoiceId,
    };

    createInvoicePayment(payload);
  };

  const yupError = (error as any)?.response?.data?.error?.inner
    ? formatYupError((error as any)?.response?.data?.error) : null;

  const normalizedErrors: string[] = yupError
    ? Object.values(yupError)
    : [(error as any)?.response?.data?.error || 'An error occurred while creating the invoice payment.'];

  const {
    mutate: downloadDocumentUpload,
  } = useDownloadDocumentUpload();

  const handleClickDownloadCheckImage = useCallback(() => {
    downloadDocumentUpload({
      id: invoicePayment?.checkImageDocumentId ?? '',
    });
  }, [downloadDocumentUpload, invoicePayment]);

  return (
    <DetailCard
      avatarAriaLabel="Payment info"
      avatarIcon={<PaymentIcon />}
      content={invoicePayment ? (
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fit, minmax(300px, 1fr))"
          sx={{ gapX: 3, gapY: 2 }}
        >
          <ShowPageDataDisplay
            label="Check Number"
            value={(
              <Box
                alignItems="center"
                display="flex"
              >
                {invoicePayment.checkNumber}

                <IconButton
                  color="primary"
                  onClick={handleClickDownloadCheckImage}
                  size="small"
                  sx={{ marginLeft: 2 }}
                >
                  <DownloadIcon />
                </IconButton>
              </Box>
              )}
          />

          <ShowPageDataDisplay
            label="Payment Amount"
            type="currency"
            value={invoicePayment.amountInCents}
          />

          <ShowPageDataDisplay
            label="Check Date"
            type="date"
            value={invoicePayment.checkDate ? new Date(invoicePayment.checkDate).toISOString().split('T')[0] : ''}
          />

          <ShowPageDataDisplay
            label="Received"
            type="datetime"
            value={new Date(invoicePayment.createdAt).toISOString()}
          />
        </Box>
      ) : (
        <>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
          >
            {() => (
              <InvoicePaymentFormFields
                invoiceId={invoiceId}
                isSubmitting={isCreatingInvoicePayment}
              />
            )}
          </Formik>

          <Collapse in={Boolean(error)}>
            <EnhancedAlert
              severity="error"
              sx={{ marginTop: 1.5 }}
              title="Error"
            >
              <ul>
                {normalizedErrors.map((err) => (
                  <li key={err}>
                    {err}
                  </li>
                ))}
              </ul>
            </EnhancedAlert>
          </Collapse>
        </>
      )}
      subheader="Payment information for the invoice"
      sx={{ marginTop: 2 }}
      title="Payment Details"
    />
  );
};

export default InvoicePaymentForm;
